import axios from 'axios'

import { IFavoriteMentorsResult } from '../hooks/apiHooks/MentorHooks'
import { IPaginatedResponse } from '../types/contextGroup'
import type { ArtifactItem, MentorWithContext } from '../types/mentor'
import { MentorBase, SessionMentor } from '../types/mentor'
import {
  FilterOptions,
  MentorPsychometrics,
  Persona,
  PersonasFilters,
} from '../types/persona'
import apiBase from '../utils/baseApi'

export interface fetchPersonasParams {
  pageNumber?: number
  pageSize: number
  filters?: PersonasFilters
}

export interface IPersonaResponse {
  data: MentorWithContext[]
  page_index: number
  total_page: number
  totalItems: number
}

export const fetchMentors = async (
  page: number,
  filters: Record<string, string | null>,
): Promise<IPersonaResponse> => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: '32',
      ...(filters.gender && { gender: filters.gender }),
      ...(filters.race && { race: filters.race }),
      ...(filters.industry && { industry: filters.industry }),
      ...(filters.jobTitle && { jobTitle: filters.jobTitle }),
      ...(filters.department && { department: filters.department }),
      ...(filters.category && { category: filters.category }),
      ...(filters.search && { search: filters.search }),
    })
    const response = await apiBase.get<IPersonaResponse>(
      `/v2/admin/mentors?${queryParams}`,
    )
    return response.data
  } catch (error) {
    console.error('Error getting favorites:', error)
    throw error
  }
}

export async function fetchFilterOptions(): Promise<FilterOptions> {
  try {
    const response = await apiBase.get<FilterOptions>('/v1/get-filters')
    return response.data
  } catch (error) {
    console.error('Error fetching filter options:', error)
    throw error
  }
}

export async function fetchPersonaById(id: string): Promise<Persona> {
  try {
    const response = await apiBase.get<Persona>(`/v1/persona/${id}`)
    return response.data
  } catch (error) {
    console.error('Error fetching persona by id:', error)
    throw error
  }
}
interface MentorProfileBySlugResponse {
  data: SessionMentor
  success: boolean
}
export async function fetchMentorProfileBySlug(
  slug: string,
): Promise<MentorProfileBySlugResponse> {
  try {
    const response = await apiBase.get<MentorProfileBySlugResponse>(
      `/v2/mentors/slug/${slug}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching companion profile by slug:', error)
    throw error
  }
}

export async function fetchMentorPsychometricsById(
  id: string,
): Promise<MentorPsychometrics> {
  try {
    const response = await apiBase.get<MentorPsychometrics>(
      `/v2/mentors/${id}/psychometrics`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching mentor psychometrics by id:', error)
    throw error
  }
}

interface FetchMentorByIdResponse extends SessionMentor {
  success: boolean
}

export async function fetchMentorById(
  id: string,
): Promise<FetchMentorByIdResponse> {
  try {
    const response = await apiBase.get<FetchMentorByIdResponse>(
      `/v2/mentors/${id}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching mentor by id:', error)
    throw error
  }
}

export async function addToFavorites(mentorId: string): Promise<{
  success: true
}> {
  try {
    const response = await apiBase.put<{
      success: true
    }>(`/v2/favourites/mentors/${mentorId}`)
    return response.data
  } catch (error) {
    console.error('Error adding to favorites:', error)
    throw error
  }
}

export async function removeFromFavorites(mentorId: string): Promise<{
  success: true
}> {
  try {
    const response = await apiBase.delete<{
      success: true
    }>(`/v2/favourites/mentors/${mentorId}`)
    return response.data
  } catch (error) {
    console.error('Error removing from favorites:', error)
    throw error
  }
}

export interface FavoriteMentorsResponse {
  success: boolean
  mentorIds: string[]
}

export async function getFavoriteMentorsIds(): Promise<
  FavoriteMentorsResponse | undefined
> {
  try {
    const response = await apiBase.get<FavoriteMentorsResponse | undefined>(
      '/v2/favourites/mentor-ids',
    )
    return response.data
  } catch (error) {
    console.error('Error getting favorites:', error)
    throw error
  }
}

export const getFavoriteMentors = async (
  page: number,
): Promise<IFavoriteMentorsResult> => {
  try {
    const response = await apiBase.get<IFavoriteMentorsResult>(
      `/v2/favourites/mentors?page_size=15&page_index=${page}`,
    )
    return response.data
  } catch (error) {
    console.error('Error getting favorites:', error)
    throw error
  }
}

export interface MentorsResponse {
  data: {
    searchMentors: {
      collection: MentorBase[]
    }
  }
}

export interface IMainCarouselMentor {
  videoLoopUrl: string | null
  previewUrl: string
  jobTitle: string
  firstName: string
  lastName: string
  mentorId: string
  slug: string
}

export type SourceType = 'Companion' | 'Context' | 'Banner'
export type VisibilityType = 'public' | 'admin' | 'visitor' | 'hidden'
export type ActionButtonType = 'url' | 'video'

export interface IMainCarouselItem {
  id: string
  title: string
  description?: string
  sourceType: SourceType
  backgroundImageUrl: string
  backgroundVideoUrl?: string | null
  visibility?: VisibilityType
  banner?: {
    button?: {
      label?: string
      url?: string
    }
  }
  actionButton?: {
    label: string
    action: ActionButtonType
    url: string
    target?: '_blank' | '_self'
  }
  companion?: {
    id: string
    slug?: string
    firstName: string
    lastName: string
    jobTitle: string
    imageUrl: string
    videoUrl?: string | null
    hideJobTitle?: boolean
  }
  context?: {
    id: string
    header: string
    query: string
  }
}

interface CarouselResponse {
  items: IMainCarouselItem[]
  updated_at: number
}

const getJsonUrl = () => {
  switch (import.meta.env.MODE) {
    case 'staging':
      return {
        mentors:
          'https://syntheticstoragestage-cdn-bcgra9aghgbrbnew.z02.azurefd.net/cache/mentors.json',
        mentors_featured:
          'https://syntheticstoragestage-cdn-bcgra9aghgbrbnew.z02.azurefd.net/cache/mentors_featured.json',
      }
    case 'production':
      return {
        mentors:
          'https://syntheticdatastorageprod-cdn-hpgkdta8a7eehggt.z02.azurefd.net/cache/mentors.json',
        mentors_featured:
          'https://syntheticdatastorageprod-cdn-hpgkdta8a7eehggt.z02.azurefd.net/cache/mentors_featured.json',
      }

    default:
      return {
        mentors:
          'https://mentordata-acazgce6cvdzgng9.z02.azurefd.net/cache/mentors.json',
        mentors_featured:
          'https://mentordata-acazgce6cvdzgng9.z02.azurefd.net/cache/mentors_featured.json',
      }
  }
}

export const getMainCarouselData = async () => {
  try {
    const response = await apiBase.get<{
      success: boolean
      data: CarouselResponse
    }>('/v2/carousels')
    return response.data?.data
  } catch (error) {
    console.error('Error fetching main carousel data:', error)
  }
}

export async function getFeaturedMentors(): Promise<MentorBase[] | undefined> {
  try {
    const { mentors_featured } = getJsonUrl()
    const response = await axios.get<MentorBase[] | undefined>(mentors_featured)
    return response.data
  } catch (error) {
    console.error('Error fetching featured mentors:', error)
  }
}

export async function getHomepageData(): Promise<MentorsResponse | undefined> {
  try {
    const { mentors } = getJsonUrl()
    const response = await axios.get<MentorsResponse | undefined>(mentors)
    return response.data
  } catch (error) {
    console.error('Error fetching homepage data:', error)
  }
}

export interface EditPersonaType {
  mentorId: string
  firstName?: string
  lastName?: string
  gender?: string
  race?: string
  jobTitle?: string
  location?: string
  industry?: string
  company?: string
  department?: string
  headline?: string
  assistantPrompt?: string
  artifactPrompts?: ArtifactItem[]
}

export interface IUpdatePsychometricsRequest {
  mentorId: string
  updatedPM: MentorPsychometrics
}

function removeTypenameFields<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(removeTypenameFields) as T
  }

  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {}

    for (const key in obj) {
      if (key !== '__typename') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        result[key] = removeTypenameFields((obj as any)[key])
      }
    }
    return result as T
  }

  return obj
}

interface FetchPromptsResponse {
  success: boolean
  avatarAssistantPrompt: string
  artifactPrompts: ArtifactItem[]
}

export const fetchPrompts = async (
  mentorId: string,
): Promise<FetchPromptsResponse | undefined> => {
  try {
    const response = await apiBase.get<FetchPromptsResponse>(
      `/v2/admin/mentors/${mentorId}/prompt-details`,
    )
    return response.data
  } catch (error) {
    console.error('Error getting assistant prompt:', error)
  }
}

export const editPersona = async (
  persona: EditPersonaType,
): Promise<EditPersonaType> => {
  try {
    const response = await apiBase.patch<EditPersonaType>(
      `/v2/mentors/${persona.mentorId}`,
      {
        mentorData: persona,
      },
    )
    return response.data
  } catch (error) {
    console.error('Error editing persona:', error)
    throw error
  }
}

export const updatePsychometrics = async (
  psychometricsData: IUpdatePsychometricsRequest,
): Promise<MentorPsychometrics> => {
  try {
    const response = await apiBase.post<MentorPsychometrics>(
      `/v2/mentors/${psychometricsData.mentorId}/psychometrics-data`,
      {
        ...removeTypenameFields(psychometricsData.updatedPM),
      },
    )
    return response.data
  } catch (error) {
    console.error('Error updating psychometrics:', error)
    throw error
  }
}

export interface IGenerateImageResponse {
  url: string
  success: boolean
}

export const generateImageByContext = async (
  userContextId: string,
): Promise<IGenerateImageResponse> => {
  try {
    const response = await apiBase.post<IGenerateImageResponse>(
      `/v2/contexts/${userContextId}/image`,
    )

    return response.data
  } catch (error) {
    console.error('Error editing persona:', error)
    throw error
  }
}

export const getResentUserContexts = async (
  page: number,
): Promise<IPaginatedResponse> => {
  try {
    const response = await apiBase.get<IPaginatedResponse>(
      `v2/contexts/recent?page_size=15&page_index=${page}`,
    )
    return response.data
  } catch (error) {
    console.error('Error getting favorites:', error)
    throw error
  }
}
export const getAssociatedMentor = async (
  contextId: string,
): Promise<MentorBase> => {
  try {
    const response = await apiBase.get<MentorBase>(
      `/v2/contexts/${contextId}/associate-mentor`,
    )
    return response.data
  } catch (error) {
    console.error('Error getting associated companion:', error)
    throw error
  }
}
